import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
} from 'recharts';
import { supabase } from '../supabase.js';
import { useAuth } from '../AuthContext.jsx';
import { markAlertRead } from '../components/AlertBits.jsx';
import { clientPath } from '../lib/clientLink.js';
import SectionHeading from '../components/HowItWorksPanel.jsx';

// Session 8 Part 2: Analytics v1, the Results tab. Every number comes from
// v_portal_results_* (nightly loader, Google data about 3 days behind). A
// null change means the comparison window is not fully covered by loaded
// data: the portal says "Not enough history yet" instead of a %.
//
// Session 8 Part 3 (Results polish): "Where visitors come from" reads in
// plain-language groups (v_portal_results_channel_groups) that expand into
// top sources and top landing pages (v_portal_results_channel_detail); the
// Google search section explains itself and shows average position as a
// stat; top lists show 5 with "Show top 10", full text, never truncated.

const TEAL = '#31BACC';
const TEAL_2 = '#268FA0';
const NAVY = '#1A1A2E';
const MUTED = '#9A9A9A';
const NOT_ENOUGH = 'Not enough history yet';

// Reader-facing explanation for each channel group (labels come from the
// SQL function _s8_channel_group; keep the two lists in step).
const GROUP_INFO = {
  'Google & other search engines': 'People who searched on Google (or Bing, etc.) and clicked your regular, unpaid listing.',
  'Direct': "People who typed your web address, used a bookmark, or clicked a link from an app or document that doesn't pass along where they came from.",
  'Social media & YouTube': 'People who clicked through from Instagram, Facebook, Pinterest, YouTube and similar, not from ads.',
  'Paid ads': 'People who clicked an ad. Cross-network means Google campaigns that run across Search, YouTube, Display and Maps at once (Performance Max).',
  'AI assistants': 'People who clicked a link to your site inside ChatGPT, Perplexity, Gemini, Claude or Copilot.',
  'Other websites': 'People who clicked a link to you on another website: directories, partners, press, blogs.',
  'Other & unknown': "Visits Google couldn't match to a source (often privacy settings or untagged links), plus small sources like email.",
};

const GOOGLE_SEARCH_INFO = 'People who searched on Google and clicked your regular (non-ad) listing to reach your site. Numbers come from Google Search Console and run about 3 days behind.';
const IMPRESSIONS_INFO = 'How many times your site appeared in Google results.';
const CLICKS_INFO = 'How many times someone clicked through to your site.';
const RARE_SEARCHES_NOTE = 'Google hides very rare searches for privacy, so this list shows the most common ones.';

const fmtNum = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 }));
const fmtPos = (n) => (n === null || n === undefined ? '—' : Number(n).toFixed(1));
const fmtDate = (d) => {
  if (!d) return '';
  const x = new Date(String(d).slice(0, 10) + 'T12:00:00');
  return Number.isNaN(x.getTime()) ? '' : x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const fmtDateTime = (d) => {
  if (!d) return '';
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? '' : x.toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};
const monthLabel = (iso) => {
  const x = new Date(String(iso).slice(0, 10) + 'T12:00:00');
  return x.toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};
const addMonths = (iso, n) => {
  const x = new Date(String(iso).slice(0, 10) + 'T12:00:00');
  x.setMonth(x.getMonth() + n);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-01`;
};
// A page as the reader sees it: the path, with "/" shown as "Home page".
const pathOf = (urlOrPath) => {
  const p = String(urlOrPath || '').replace(/^https?:\/\/[^/]+/, '').replace(/[?#].*$/, '');
  return p === '' || p === '/' ? 'Home page' : p;
};

function Change({ pct, className = '' }) {
  if (pct === null || pct === undefined) return <span className={`delta delta-none ${className}`}>{NOT_ENOUGH}</span>;
  const v = Number(pct);
  if (v === 0) return <span className={`delta delta-flat ${className}`}>– 0%</span>;
  return (
    <span className={`delta ${v > 0 ? 'delta-up' : 'delta-down'} ${className}`}>
      {v > 0 ? '▲' : '▼'} {Math.abs(v).toLocaleString('en-US', { maximumFractionDigits: 0 })}%
    </span>
  );
}

// Average position: a lower number is better, so a negative change is the
// good direction ("up 2.3 places", positive colour).
function PositionChange({ change }) {
  if (change === null || change === undefined) return <span className="delta delta-none">{NOT_ENOUGH}</span>;
  const v = Number(change);
  if (v === 0) return <span className="delta delta-flat">– no change</span>;
  const places = Math.abs(v).toFixed(1);
  return v < 0
    ? <span className="delta delta-up">▲ up {places} places</span>
    : <span className="delta delta-down">▼ down {places} places</span>;
}

// ⓘ with an explanation on hover, focus or tap.
function Info({ text, label = 'What this means' }) {
  const [open, setOpen] = useState(false);
  return (
    <span className={`info ${open ? 'open' : ''}`} onMouseLeave={() => setOpen(false)}>
      <button type="button" className="info-btn" aria-label={label} aria-expanded={open}
              onClick={(e) => { e.stopPropagation(); setOpen((o) => !o); }} onBlur={() => setOpen(false)}>
        ⓘ
      </button>
      <span className="info-tip" role="tooltip">{text}</span>
    </span>
  );
}

function Tile({ label, value, pct, lyPct, note }) {
  return (
    <div className="tile">
      <div className="tile-label">{label}</div>
      <div className="tile-value">{value}</div>
      <div className="tile-delta"><Change pct={pct} /> <span className="tile-vs">vs the 28 days before</span></div>
      {lyPct !== null && lyPct !== undefined && (
        <div className="tile-ly"><Change pct={lyPct} /> vs last year</div>
      )}
      {note && <div className="tile-note">{note}</div>}
    </div>
  );
}

function Section({ title, sub, info, children }) {
  return (
    <section className="results-section">
      <div className="section-heading">
        <h2>{title}{info && <Info text={info} label={`What "${title}" means`} />}</h2>
        {sub && <span className="count">{sub}</span>}
      </div>
      {children}
    </section>
  );
}

function Empty({ children }) {
  return <p className="results-empty">{children}</p>;
}

export default function Results() {
  const { activeId, activeClient, isAgency, entitlements } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const hasBlogs = (entitlements || []).includes('seo_blogs');
  const hasAnalytics = (entitlements || []).includes('analytics');
  // Session 37: the page is open from the day they pay. Whether it has
  // anything IN it depends on us having access, and saying so beats a locked
  // door with no explanation.
  const [access, setAccess] = useState(null);
  useEffect(() => {
    if (!activeId) return;
    (async () => {
      const { data } = await supabase
        .from('v_portal_analytics_access').select('*').eq('client_id', activeId).maybeSingle();
      setAccess(data || null);
    })();
  }, [activeId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setData(null); setError(null);
      if (!activeId || !hasAnalytics) return;
      const q = (view, order) => {
        let s = supabase.from(view).select('*').eq('client_id', activeId);
        if (order) s = s.order(order.col, { ascending: order.asc !== false });
        return s;
      };
      const [head, monthly, groups, detail, top, blog, fresh, alerts, plan] = await Promise.all([
        q('v_portal_results_headline').maybeSingle(),
        q('v_portal_results_monthly', { col: 'month' }),
        q('v_portal_results_channel_groups', { col: 'sort_order' }),
        q('v_portal_results_channel_detail', { col: 'rank' }),
        q('v_portal_results_top', { col: 'rank' }),
        hasBlogs ? q('v_portal_results_blog', { col: 'publish_date', asc: false }) : Promise.resolve({ data: [] }),
        q('v_portal_results_freshness'),
        isAgency ? q('v_portal_results_alerts', { col: 'created_at', asc: false }) : Promise.resolve({ data: [] }),
        hasBlogs ? q('v_portal_plan', { col: 'publish_date' }) : Promise.resolve({ data: [] }),
      ]);
      if (cancelled) return;
      const firstErr = [head, monthly, groups, detail, top, blog, fresh, alerts, plan].find((r) => r && r.error);
      if (firstErr) { setError(firstErr.error.message); return; }
      setData({
        head: head.data || null, monthly: monthly.data || [], groups: groups.data || [], detail: detail.data || [],
        top: top.data || [], blog: blog.data || [], fresh: fresh.data || [], alerts: alerts.data || [], plan: plan.data || [],
      });
    })();
    return () => { cancelled = true; };
  }, [activeId, hasAnalytics, hasBlogs, isAgency]);

  // Session 37: open, and waiting on access. Everything a customer needs to fix
// it themselves, and a way to tell us they have — which raises a to-do for the
// agency rather than switching anything on, because a client ticking a box is
// not a working connection.
function NeedsAccess({ access, clientName, onDone, isAgency, clientId }) {
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  const [sent, setSent] = useState(false);
  const [err, setErr] = useState(null);
  const claimed = !!access.access_claimed_at;

  async function claim() {
    setBusy(true); setErr(null);
    const { error } = await supabase.rpc('portal_claim_analytics_access', {
      p_notes: note.trim() || null,
    });
    setBusy(false);
    if (error) { setErr(error.message); return; }
    setSent(true);
    onDone?.();
  }

  return (
    <>
      <SectionHeading title="Results" explainer="analytics" label="your results" />
      <p className="lede">
        This is where {clientName || 'your'} numbers will appear — how many people
        find you, what they search for, and which pages they land on.
      </p>

      {(sent || claimed) ? (
        <div className="banner banner-info">
          <strong>Thanks — we're checking.</strong> We have been told access is
          granted and someone here is confirming it works. Once it does, this page
          fills in overnight and you will not need to do anything else.
        </div>
      ) : (
        <div className="banner banner-gate">
          <strong>We don't have access to your Google accounts yet.</strong> That is
          the only thing standing between you and this page. It takes about five
          minutes and the steps are below.
        </div>
      )}

      <section className="review-section">
        <h2>Google Analytics</h2>
        <p className="hint">
          This is what tells us how many people visit and what they do.
          {access.ga4_property_id
            ? ` You gave us property ${access.ga4_property_id}.`
            : ' We do not have a property ID from you yet.'}
        </p>
        <ol className="onb-steps">
          <li>In Google Analytics, open <strong>Admin</strong> (the cog, bottom left).</li>
          <li>Under Property, choose <strong>Property access management</strong>.</li>
          <li>Click <strong>+</strong>, then <strong>Add users</strong>, and enter{' '}
            <code>ops@digitallotusmarketing.com</code>.</li>
          <li>Give it <strong>Viewer</strong> and click <strong>Add</strong>.</li>
        </ol>
      </section>

      <section className="review-section">
        <h2>Google Search Console</h2>
        <p className="hint">
          This is what tells us which searches you are showing up for.
          {access.gsc_property
            ? ` You gave us ${access.gsc_property}.`
            : ' We do not have your property from you yet.'}
        </p>
        <ol className="onb-steps">
          <li>In Search Console, pick your site, then <strong>Settings</strong> →{' '}
            <strong>Users and permissions</strong>.</li>
          <li>Click <strong>Add user</strong> and enter{' '}
            <code>ops@digitallotusmarketing.com</code>.</li>
          <li>Set permission to <strong>Full</strong>, then <strong>Add</strong>.</li>
        </ol>
      </section>

      {isAgency && (
        <section className="review-section">
          <h2>You are seeing this as the agency</h2>
          <p className="hint">
            The button below is for the client; it would stamp your own record,
            not theirs. The connection is checked from this client's settings page,
            Analytics section: save the property ids there and the check runs, or
            press Check again.
          </p>
          <div className="review-actions" style={{ justifyContent: 'flex-start' }}>
            <Link className="btn" to={`/admin/clients/${clientId}/settings`}>Open their settings</Link>
          </div>
        </section>
      )}

      {!isAgency && !(sent || claimed) && (
        <section className="review-section">
          <h2>Done it?</h2>
          <p className="hint">
            Tell us and we will check it from our end. If anything is missing we
            will come back to you rather than leaving you wondering.
          </p>
          <label className="field">
            <span className="field-label">Anything we should know? (optional)</span>
            <textarea className="review-textarea" rows={2} value={note} maxLength={4000}
                      placeholder="I could not find Search Console, or: I added you to Analytics only."
                      onChange={(e) => setNote(e.target.value)} />
          </label>
          {err && <div className="banner banner-err">{err}</div>}
          <div className="review-actions" style={{ justifyContent: 'flex-start' }}>
            <button type="button" className="btn btn-primary" disabled={busy} onClick={claim}>
              {busy ? 'Sending…' : "I've given you access"}
            </button>
          </div>
        </section>
      )}

      {!isAgency && (sent || claimed) && (
        <div className="review-actions" style={{ justifyContent: 'flex-start' }}>
          <button type="button" className="link-btn" disabled={busy} onClick={claim}>
            Tell us again, or add a note
          </button>
        </div>
      )}
    </>
  );
}

// Traffic over time: last 13 months, with the same month last year as a dashed line.
  const traffic = useMemo(() => {
    if (!data) return { rows: [], hasLy: false };
    const byMonth = Object.fromEntries(data.monthly.map((m) => [String(m.month).slice(0, 10), m]));
    const months = Object.keys(byMonth).sort();
    if (!months.length) return { rows: [], hasLy: false };
    const last = months[months.length - 1];
    const rows = [];
    for (let i = 12; i >= 0; i -= 1) {
      const key = addMonths(last, -i);
      const cur = byMonth[key];
      const ly = byMonth[addMonths(key, -12)];
      rows.push({
        key, label: monthLabel(key),
        sessions: cur?.sessions ?? null,
        lastYear: ly?.sessions ?? null,
        clicks: cur?.gsc_clicks ?? null,
        impressions: cur?.gsc_impressions ?? null,
        position: cur?.gsc_position ?? null,
        partial: cur ? (cur.ga4_days > 0 && cur.ga4_days < 28 && i === 0) : false,
      });
    }
    return { rows, hasLy: rows.some((r) => r.lastYear !== null) };
  }, [data]);

  if (!hasAnalytics) {
    return (
      <div className="empty">
        <h2>Results isn't switched on for this client yet</h2>
        <p>Ask us about it and we'll turn it on.</p>
      </div>
    );
  }
  // Session 37: open, but we cannot fill it yet. This is the common case for a
  // brand-new customer and it needs saying plainly, with the steps right here
  // rather than in an email they have to go and find.
  if (access && !access.connected) {
    return <NeedsAccess access={access} clientName={activeClient?.name} isAgency={isAgency} clientId={activeId} onDone={() => {
      // Re-read rather than guessing: the claim does not make it connected, so
      // the page should show the "we are checking" state, not the data.
      supabase.from('v_portal_analytics_access').select('*')
        .eq('client_id', activeId).maybeSingle()
        .then(({ data }) => setAccess(data || null));
    }} />;
  }
  if (error) return <div className="empty"><h2>Couldn't load results</h2><p>{error}</p></div>;
  if (!data) return <p className="lede">Loading results…</p>;

  const h = data.head;
  const ga4Loaded = !!(h && h.ga4_window_end);
  const gscLoaded = !!(h && h.gsc_window_end);
  const windowText = ga4Loaded
    ? `${fmtDate(new Date(new Date(h.ga4_window_end + 'T12:00:00').getTime() - 27 * 86400000))} to ${fmtDate(h.ga4_window_end)}`
    : '';
  const latestRun = data.fresh.map((f) => f.last_run_at).filter(Boolean).sort().pop();
  const latestDate = data.fresh.map((f) => f.latest_date).filter(Boolean).sort().pop();
  const landing = data.top.filter((r) => r.kind === 'landing');
  const queries = data.top.filter((r) => r.kind === 'query');
  const pages = data.top.filter((r) => r.kind === 'gsc_page');
  const nextPost = data.plan.find((p) => !['Published', 'Ready to post'].includes(p.status) && p.publish_date);

  return (
    <>
      <SectionHeading title="Results" explainer="analytics" label="your results" />
      <p className="lede">
        How {activeClient?.name || 'your site'} is doing in Google, in plain numbers.
        {windowText && <> Last 28 days means {windowText}.</>}
      </p>

      {isAgency && data.alerts.length > 0 && (
        <ResultsAlerts alerts={data.alerts}
                       onChange={(id, isRead) => setData((d) => ({ ...d, alerts: d.alerts.map((a) => (a.id === id ? { ...a, is_read: isRead } : a)) }))} />
      )}

      {/* 1. Headline tiles */}
      {!ga4Loaded && !gscLoaded ? (
        <Empty>No Google data loaded yet. The first numbers appear the morning after we connect the account.</Empty>
      ) : (
        <div className="tiles">
          <Tile label="Visitors (sessions)" value={ga4Loaded ? fmtNum(h.sessions_28) : '—'} pct={ga4Loaded ? h.sessions_pct : null}
                lyPct={h.sessions_ly_pct} note={ga4Loaded && h.ga4_days < 28 ? `${h.ga4_days} days loaded so far` : null} />
          <Tile label="Clicks from Google search" value={gscLoaded ? fmtNum(h.clicks_28) : '—'} pct={gscLoaded ? h.clicks_pct : null}
                lyPct={h.clicks_ly_pct} note={gscLoaded && h.gsc_days < 28 ? `${h.gsc_days} days loaded so far` : null} />
          <Tile label="Key actions (calls, forms, bookings)" value={ga4Loaded ? fmtNum(h.key_events_28) : '—'} pct={ga4Loaded ? h.key_events_pct : null} />
          <Tile label="Visits from AI assistants" value={ga4Loaded ? fmtNum(h.ai_sessions_28) : '—'} pct={ga4Loaded ? h.ai_sessions_pct : null} />
        </div>
      )}

      {/* 2. Traffic over time */}
      <Section title="Traffic over time" sub="monthly visitors">
        {traffic.rows.every((r) => r.sessions === null) ? (
          <Empty>No visitor history loaded yet.</Empty>
        ) : (
          <div className="chart-card">
            <ResponsiveContainer width="100%" height={260}>
              <ComposedChart data={traffic.rows} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke="#EFEAE3" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: MUTED }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: MUTED }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip formatter={(v, name) => [fmtNum(v), name]} labelStyle={{ color: NAVY }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="sessions" name="Visitors" fill={TEAL} radius={[4, 4, 0, 0]} />
                {traffic.hasLy && <Line type="monotone" dataKey="lastYear" name="Same month last year" stroke={NAVY} strokeDasharray="5 4" dot={false} connectNulls />}
              </ComposedChart>
            </ResponsiveContainer>
            {traffic.rows[traffic.rows.length - 1]?.partial && <p className="hint">The latest month is still filling in.</p>}
          </div>
        )}
      </Section>

      {/* 3. Where visitors come from */}
      <Section title="Where visitors come from" sub="last 28 days">
        {data.groups.length === 0 ? (
          <Empty>No channel data loaded yet.</Empty>
        ) : (
          <ChannelGroups groups={data.groups} detail={data.detail} />
        )}
      </Section>

      {/* 4. Google search */}
      <Section title="Google search" sub="last 28 days" info={GOOGLE_SEARCH_INFO}>
        {!gscLoaded ? (
          <Empty>No Google Search data loaded yet.</Empty>
        ) : (
          <>
            <div className="search-stats">
              <div className="search-stat">
                <div className="search-stat-label">Clicks (last 28 days)<Info text={CLICKS_INFO} label="What clicks means" /></div>
                <div className="search-stat-value">{fmtNum(h.clicks_28)}</div>
                <div className="search-stat-delta"><Change pct={h.clicks_pct} /> <span className="tile-vs">vs the 28 days before</span></div>
              </div>
              <div className="search-stat">
                <div className="search-stat-label">Impressions (last 28 days)<Info text={IMPRESSIONS_INFO} label="What impressions means" /></div>
                <div className="search-stat-value">{fmtNum(h.impressions_28)}</div>
                <div className="search-stat-delta"><Change pct={h.impressions_pct} /> <span className="tile-vs">vs the 28 days before</span></div>
              </div>
              <div className="search-stat search-stat-position">
                <div className="search-stat-label">Average position (last 28 days)</div>
                <div className="search-stat-value">{fmtPos(h.position_28)}</div>
                <div className="search-stat-delta"><PositionChange change={h.position_change} /> <span className="tile-vs">vs the 28 days before</span></div>
                <div className="search-stat-note">1 = the top result on Google. Lower is better; positions 1–10 are page one.</div>
              </div>
            </div>
            {traffic.rows.every((r) => r.clicks === null) ? null : (
              <div className="chart-card">
                <ResponsiveContainer width="100%" height={240}>
                  <ComposedChart data={traffic.rows} margin={{ top: 8, right: 0, left: -12, bottom: 0 }}>
                    <CartesianGrid vertical={false} stroke="#EFEAE3" />
                    <XAxis dataKey="label" tick={{ fontSize: 12, fill: MUTED }} axisLine={false} tickLine={false} />
                    <YAxis yAxisId="left" tick={{ fontSize: 12, fill: MUTED }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: MUTED }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip formatter={(v, name) => [fmtNum(v), name]} labelStyle={{ color: NAVY }} />
                    <Legend wrapperStyle={{ fontSize: 12 }} />
                    <Bar yAxisId="left" dataKey="clicks" name="Clicks" fill={TEAL_2} radius={[4, 4, 0, 0]} />
                    <Line yAxisId="right" type="monotone" dataKey="impressions" name="Impressions (times shown)" stroke={NAVY} dot={false} connectNulls />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}
          </>
        )}
        {/* Session 8 Part 4: each list is its own full-width block */}
        <TopTable title="Top searches" note={RARE_SEARCHES_NOTE} rows={queries} nameLabel="Search phrase" valueLabel="Clicks" showImpr showPos />
        <TopTable title="Top pages in Google" rows={pages} nameLabel="Page" valueLabel="Clicks" showImpr showPos isUrl />
      </Section>

      {/* 5. Top landing pages */}
      <Section title="Top landing pages" sub="where visits start, last 28 days">
        <TopTable rows={landing} nameLabel="Page" valueLabel="Visitors" showChange isPath />
      </Section>

      {/* 6. Blog results */}
      {hasBlogs && (
        <Section title="Blog results">
          {data.blog.length === 0 ? (
            <Empty>
              {nextPost
                ? <>Your first post goes live on {fmtDate(nextPost.publish_date)}. Results appear about a week after.</>
                : <>No posts are live yet. Results appear about a week after the first one publishes.</>}
            </Empty>
          ) : (
            <BlogResults posts={data.blog} ga4Loaded={ga4Loaded} />
          )}
        </Section>
      )}

      <p className="results-footer">
        {latestRun ? <>Updated {fmtDateTime(latestRun)}{latestDate ? `, data through ${fmtDate(latestDate)}` : ''}.</> : 'Not updated yet.'}
        {' '}Google data runs about 3 days behind.
      </p>
    </>
  );
}

// Blog results: totals across every live post (Google impressions and clicks
// from Search Console, visitors from GA4), the top 5 posts by times shown in
// Google, and the full per-post detail behind "See all posts".
//
// Session 67: the totals used to be one line of impressions and clicks, and
// visitors only appeared inside "See all posts" even though the view has
// carried sessions_since per post since Session 8. Leena asked for visitors
// next to the Google numbers, and for the totals to stand at the top.
const byGoogle = (a, b) => (Number(b.impressions_since) || 0) - (Number(a.impressions_since) || 0)
  || (Number(b.clicks_since) || 0) - (Number(a.clicks_since) || 0);

const BLOG_SHOWN_INFO = 'How many times your blog posts appeared in Google search results, added up since each post went live. From Google Search Console.';
const BLOG_CLICKS_INFO = 'How many times someone clicked a blog post in Google search results, since each post went live. From Google Search Console.';
const BLOG_VISITORS_INFO = 'Visits that started on a blog post, from any source: Google, social media, email, AI assistants or a direct link. From Google Analytics (GA4), since each post went live.';

function BlogResults({ posts, ga4Loaded }) {
  const [all, setAll] = useState(false);
  const sum = (key) => posts.reduce((t, b) => t + (Number(b[key]) || 0), 0);
  const shown = sum('impressions_since');
  const clicks = sum('clicks_since');
  const visitors = sum('sessions_since');
  const top = [...posts].sort(byGoogle).slice(0, 5);
  return (
    <>
      <p className="blog-summary">
        {fmtNum(posts.length)} live post{posts.length === 1 ? '' : 's'}
      </p>
      <p className="hint blog-summary-caption">Totals since each post went live.</p>

      <div className="search-stats blog-totals">
        <div className="search-stat">
          <div className="search-stat-label">Times shown in Google<Info text={BLOG_SHOWN_INFO} label="What times shown means" /></div>
          <div className="search-stat-value">{fmtNum(shown)}</div>
          <div className="search-stat-note">Google organic search</div>
        </div>
        <div className="search-stat">
          <div className="search-stat-label">Clicks from Google<Info text={BLOG_CLICKS_INFO} label="What clicks means" /></div>
          <div className="search-stat-value">{fmtNum(clicks)}</div>
          <div className="search-stat-note">Google organic search</div>
        </div>
        <div className="search-stat">
          <div className="search-stat-label">Visitors to your posts<Info text={BLOG_VISITORS_INFO} label="What visitors means" /></div>
          <div className="search-stat-value">{ga4Loaded ? fmtNum(visitors) : '—'}</div>
          <div className="search-stat-note">{ga4Loaded ? 'All sources, from Google Analytics' : 'Connect Google Analytics to see visitors'}</div>
        </div>
      </div>

      <div className="top-table">
        <h3>Top posts</h3>
        <div className="card results-table blog-top" role="table">
          <div className="results-row results-head" role="row">
            <span>Post</span><span>Times shown</span><span>Clicks</span><span>Visitors</span>
          </div>
          {top.map((b) => (
            <div className="results-row" role="row" key={b.slug}>
              <span className="name">
                {b.url ? <a href={b.url} target="_blank" rel="noopener noreferrer">{b.title || b.slug}</a> : (b.title || b.slug)}
              </span>
              {Number(b.impressions_since) > 0 ? (
                <>
                  <span className="num" data-label="Times shown">{fmtNum(b.impressions_since)}</span>
                  <span className="num" data-label="Clicks">{fmtNum(b.clicks_since)}</span>
                </>
              ) : (
                <span className="not-yet">Not in Google yet</span>
              )}
              <span className="num" data-label="Visitors">{ga4Loaded ? fmtNum(b.sessions_since) : '—'}</span>
            </div>
          ))}
        </div>
      </div>

      <button type="button" className="link-btn blog-toggle" aria-expanded={all} onClick={() => setAll((o) => !o)}>
        {all ? 'Hide all posts' : 'See all posts'}
      </button>
      {all && <BlogDetail posts={posts} />}
    </>
  );
}

// The per-post detail, as it was before the summary (Session 8 Part 2).
function BlogDetail({ posts }) {
  return (
    <div className="blog-results">
      {posts.map((b) => (
        <div className="card blog-result" key={b.slug}>
          <div className="blog-result-head">
            <div>
              <div className="blog-result-title">{b.title || b.slug}</div>
              <div className="hint" style={{ margin: 0 }}>Live since {fmtDate(b.publish_date)}{b.url && <> · <a href={b.url} target="_blank" rel="noopener noreferrer">View</a></>}</div>
            </div>
          </div>
          <div className="blog-result-stats">
            <div><span className="stat">{fmtNum(b.sessions_since)}</span><span className="stat-label">visitors since launch</span></div>
            <div><span className="stat">{fmtNum(b.sessions_28)}</span><span className="stat-label">visitors last 28 days</span></div>
            <div><span className="stat">{fmtNum(b.clicks_since)}</span><span className="stat-label">Google clicks since launch</span></div>
            <div><span className="stat">{fmtNum(b.impressions_since)}</span><span className="stat-label">times shown in Google</span></div>
            <div><span className="stat">{fmtPos(b.position_28)}</span><span className="stat-label">avg position, last 28 days</span></div>
          </div>
        </div>
      ))}
    </div>
  );
}

// "Where visitors come from": one row per group, click to expand into the
// top sources and the top landing pages for that group.
function ChannelGroups({ groups, detail }) {
  const [open, setOpen] = useState(null);
  const toggle = (label) => setOpen((o) => (o === label ? null : label));
  return (
    <div className="card results-table channel-groups" role="table">
      <div className="results-row results-head channel-row" role="row">
        <span>Source</span><span>Visitors</span><span>Share</span><span>vs 28 days before</span><span>vs last year</span><span>&nbsp;</span>
      </div>
      {groups.map((g) => {
        const isOpen = open === g.group_label;
        return (
          <div key={g.group_label} className={`channel-group ${isOpen ? 'open' : ''}`}>
            <div className="results-row channel-row" role="row" onClick={() => toggle(g.group_label)}
                 onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(g.group_label); } }}
                 tabIndex={0} aria-expanded={isOpen}>
              <span className="name">{g.group_label}{GROUP_INFO[g.group_label] && <Info text={GROUP_INFO[g.group_label]} label={`What "${g.group_label}" means`} />}</span>
              <span className="num" data-label="Visitors">{fmtNum(g.sessions_28)}</span>
              <span className="num" data-label="Share">{Number(g.share_pct).toLocaleString('en-US', { maximumFractionDigits: 0 })}%</span>
              <span data-label="vs 28 days before"><Change pct={g.pct_change} /></span>
              <span data-label="vs last year"><Change pct={g.pct_change_ly} /></span>
              <span className="chevron" aria-hidden="true">{isOpen ? '▴' : '▾'}</span>
            </div>
            {isOpen && (
              <div className="channel-detail">
                <DetailList title="Top sources" rows={detail.filter((d) => d.group_label === g.group_label && d.kind === 'source')}
                            empty="No source detail for this group yet." />
                <DetailList title="Top pages they landed on" rows={detail.filter((d) => d.group_label === g.group_label && d.kind === 'page')}
                            empty="No page detail for this group yet." />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function DetailList({ title, rows, empty }) {
  const [all, setAll] = useState(false);
  const shown = all ? rows.slice(0, 10) : rows.slice(0, 5);
  return (
    <div className="detail-list">
      <h4>{title}</h4>
      {rows.length === 0 ? (
        <p className="hint" style={{ margin: 0 }}>{empty}</p>
      ) : (
        <>
          <ul>
            {shown.map((r) => (
              <li key={`${r.kind}-${r.rank}`}>
                <span className="detail-name" title={r.name !== r.display_name ? r.name : undefined}>{r.display_name}</span>
                <span className="detail-num">{fmtNum(r.sessions_28)}</span>
              </li>
            ))}
          </ul>
          {rows.length > 5 && !all && (
            <button type="button" className="link-btn" onClick={() => setAll(true)}>Show 10</button>
          )}
        </>
      )}
    </div>
  );
}

// Top 5 with "Show top 10". Names show in full and wrap; on a phone the
// numbers sit on a second line under the name (see styles.css).
function TopTable({ title, note, rows, nameLabel = '', valueLabel, showImpr = false, showPos = false, showChange = false, isUrl = false, isPath = false }) {
  const [all, setAll] = useState(false);
  const shown = all ? rows.slice(0, 10) : rows.slice(0, 5);
  const cols = 1 + 1 + (showImpr ? 1 : 0) + (showPos ? 1 : 0) + (showChange ? 1 : 0);
  return (
    <div className="top-table">
      {title && <h3>{title}</h3>}
      {note && <p className="hint top-note">{note}</p>}
      {rows.length === 0 ? (
        <Empty>Nothing to show yet.</Empty>
      ) : (
        <div className={`card results-table top-cols-${cols}`} role="table">
          <div className="results-row results-head" role="row">
            <span>{nameLabel}</span><span>{valueLabel}</span>
            {showImpr && <span>Impressions</span>}
            {showPos && <span>Avg position</span>}
            {showChange && <span>vs 28 days before</span>}
          </div>
          {shown.map((r) => (
            <div className="results-row" role="row" key={`${r.kind}-${r.rank}`}>
              <span className="name">
                {isUrl
                  ? <a href={r.name} target="_blank" rel="noopener noreferrer">{pathOf(r.name)}</a>
                  : (isPath ? pathOf(r.name) : r.name)}
              </span>
              <span className="num" data-label={valueLabel}>{fmtNum(r.value_28)}</span>
              {showImpr && <span className="num" data-label="Impressions">{fmtNum(r.impressions_28)}</span>}
              {showPos && <span className="num" data-label="Avg position">{fmtPos(r.position_28)}</span>}
              {showChange && <span data-label="vs 28 days before"><Change pct={r.pct_change} /></span>}
            </div>
          ))}
          {rows.length > 5 && !all && (
            <div className="results-more">
              <button type="button" className="link-btn" onClick={() => setAll(true)}>Show top 10</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { Change };
// Session 14: the agency alert strip (this client only) with per-user read
// state. Unread alerts show with "Mark as read"; read ones fold into one
// "N earlier alerts (read)" toggle and show dimmed when opened.
function ResultsAlerts({ alerts, onChange }) {
  const { bumpInbox } = useAuth();
  const [showRead, setShowRead] = useState(false);
  const [busy, setBusy] = useState(false);
  const unread = alerts.filter((a) => !a.is_read);
  const read = alerts.filter((a) => a.is_read);
  const mark = async (a) => {
    setBusy(true);
    const { error } = await markAlertRead(a.id);
    setBusy(false);
    if (!error) { onChange(a.id, true); bumpInbox(); }
  };
  const pill = (a, isRead) => (
    <span key={a.id} className={`alert-pill ${isRead ? 'alert-pill-read' : ''}`}>
      {a.channel || 'Traffic'} down {Math.abs(Math.round(a.pct_change))}%: {fmtNum(a.current_value)} vs {fmtNum(a.prior_value)} the 28 days before
      {a.created_at ? ` · ${fmtDate(String(a.created_at).slice(0, 10))}` : ''}
      {!isRead && <button type="button" className="alert-pill-btn" disabled={busy} onClick={() => mark(a)}>Mark as read</button>}
    </span>
  );
  return (
    <div className="alert-strip" role="status">
      <strong>Alerts</strong>
      {unread.map((a) => pill(a, false))}
      {read.length > 0 && (
        <button type="button" className="alert-pill-btn" aria-expanded={showRead} onClick={() => setShowRead((v) => !v)}>
          {showRead ? '▾' : '▸'} {read.length} earlier alert{read.length === 1 ? '' : 's'} (read)
        </button>
      )}
      {showRead && read.map((a) => pill(a, true))}
      <span className="hint" style={{ margin: 0 }}>Agency only. Clears itself when the numbers recover.</span>
    </div>
  );
}

export function ResultsHomeTile() {
  // Client home: one tile, "Visitors last 28 days: N (±x%)", linking to Results.
  const { activeId, entitlements } = useAuth();
  const [row, setRow] = useState(null);
  const enabled = (entitlements || []).includes('analytics');
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setRow(null);
      if (!activeId || !enabled) return;
      const { data } = await supabase.from('v_portal_results_headline').select('sessions_28, sessions_pct, ga4_window_end').eq('client_id', activeId).maybeSingle();
      if (!cancelled) setRow(data || false);
    })();
    return () => { cancelled = true; };
  }, [activeId, enabled]);
  if (!enabled || row === null || row === false) return null;
  return (
    <Link to={clientPath('/results', activeId)} className="home-tile">
      <span className="home-tile-label">Visitors last 28 days</span>
      <span className="home-tile-value">{row.ga4_window_end ? fmtNum(row.sessions_28) : '—'}</span>
      <span className="home-tile-delta">{row.ga4_window_end ? <Change pct={row.sessions_pct} /> : 'No data loaded yet'}</span>
      <span className="home-tile-cta">See results →</span>
    </Link>
  );
}
